/**
 * F45 (Wave 2D) — analytics dashboard helpers: ISO date math (Asia/Beirut-naive,
 * date-only — the analytics range is whole calendar days, no time component),
 * quick-period preset → range resolution, refresh-time formatting, and KPI
 * delta-tone logic. Mirrors the F44 `financeHelpers` shape.
 */
import type { AnalyticsPeriod } from '@bravemove/types';

export interface AnalyticsRangeValue {
  readonly from: string;
  readonly to: string;
}

/** Quick-select presets surfaced as a Chip row (the contract's ANALYTICS_PERIODS). */
export type AnalyticsQuickPreset = AnalyticsPeriod;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Format a Date as `YYYY-MM-DD` (no timezone shift — uses local Y/M/D). */
function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** N days ago from `base` (default today), as `YYYY-MM-DD`. */
function daysAgo(n: number, base = new Date()): string {
  const d = new Date(base);
  d.setDate(d.getDate() - n);
  return isoDate(d);
}

/** Default analytics range = trailing 30 days (D8). */
export function defaultAnalyticsRange(base = new Date()): AnalyticsRangeValue {
  return { from: daysAgo(29, base), to: isoDate(base) };
}

/**
 * Resolve a quick-preset to a concrete range. `custom` returns null (the page
 * keeps the existing range + shows the DateRangePicker).
 */
export function resolvePreset(
  preset: AnalyticsQuickPreset,
  base = new Date(),
): AnalyticsRangeValue | null {
  const today = isoDate(base);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case '7d':
      return { from: daysAgo(6, base), to: today };
    case '30d':
      return { from: daysAgo(29, base), to: today };
    case '90d':
      return { from: daysAgo(89, base), to: today };
    case 'ytd':
      return { from: `${base.getFullYear()}-01-01`, to: today };
    case 'custom':
      return null;
    default:
      return null;
  }
}

/** Format an ISO timestamp into a locale time string for the refresh banner. */
export function formatRefreshTime(iso: string | null, locale: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(d);
  } catch {
    return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit' }).format(d);
  }
}

/** Map a signed delta to a Stat tone. A positive delta is "positive" (green). */
export function deltaTone(deltaPercent: number): 'positive' | 'negative' | 'neutral' {
  if (deltaPercent > 0) return 'positive';
  if (deltaPercent < 0) return 'negative';
  return 'neutral';
}

/**
 * Round a percent value to 1 decimal for display. The backend emits raw float
 * rates/deltas (e.g. 33.33333333333333) — rounding happens at render time
 * (the percent-values-unrounded audit fix).
 */
export function roundPercent(value: number): number {
  return Math.round(value * 10) / 10;
}

/** The 7 day-of-week abbreviations (0=Sun..6=Sat) for the activity heatmap. */
export const DOW_ABBREV: readonly string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
